import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const I18nContext = createContext(null);

const STRINGS = {
    en: {
        common: { home: "Home", login: "Login", signup: "Sign Up", logout: "Logout",
            dashboard: "Dashboard", profile: "Profile", settings: "Settings",
            language: "Language", theme: "Theme", light: "Light", dark: "Dark",
            cancel: "Cancel", save: "Save", confirm: "Confirm", continue: "Continue",
            search: "Search", clear_all: "Clear all" },
        nav: { how_it_works: "How It Works", gift_cards: "Gift Cards", rates: "Rates",
            faq: "FAQ", contact: "Contact" },
        landing: { exchange_now: "Exchange Now", you_send: "You Send", you_receive: "You Receive" },
        send: { select_card: "Select Card", card_details: "Card Details", payout: "Payout",
            face_value: "Face value", security_pin: "Security PIN", receipt: "Receipt of purchase",
            payout_method: "Payout method", you_will_receive: "You'll receive", submit: "Submit Exchange" },
        notifications: { title: "Notifications", empty: "You're all caught up!",
            mark_all_read: "Mark all read", clear_all: "Clear all" },
        profile: { member_since: "Member since", last_login: "Last login",
            total_exchanges: "Total exchanges", completed: "Completed", pending: "Pending",
            rejected: "Rejected", volume: "Volume", avg_rate: "Avg rate", country: "Country",
            language: "Language", theme: "Theme" },
    },
    es: {
        common: { home: "Inicio", login: "Ingresar", signup: "Registrarse", logout: "Salir",
            dashboard: "Panel", profile: "Perfil", settings: "Ajustes",
            language: "Idioma", theme: "Tema", light: "Claro", dark: "Oscuro",
            cancel: "Cancelar", save: "Guardar", confirm: "Confirmar", continue: "Continuar",
            search: "Buscar", clear_all: "Borrar todo" },
        nav: { how_it_works: "Cómo Funciona", gift_cards: "Tarjetas", rates: "Tarifas",
            faq: "FAQ", contact: "Contacto" },
        landing: { exchange_now: "Cambiar Ahora", you_send: "Tú Envías", you_receive: "Tú Recibes" },
        send: { select_card: "Selecciona Tarjeta", card_details: "Detalles", payout: "Pago",
            face_value: "Valor", security_pin: "PIN de seguridad", receipt: "Recibo de compra",
            payout_method: "Método de pago", you_will_receive: "Recibirás", submit: "Enviar Cambio" },
        notifications: { title: "Notificaciones", empty: "¡Estás al día!",
            mark_all_read: "Marcar todas leídas", clear_all: "Borrar todo" },
        profile: { member_since: "Miembro desde", last_login: "Último acceso",
            total_exchanges: "Cambios", completed: "Completados", pending: "Pendientes",
            rejected: "Rechazados", volume: "Volumen", avg_rate: "Tasa media", country: "País",
            language: "Idioma", theme: "Tema" },
    },
    fr: {
        common: { home: "Accueil", login: "Connexion", signup: "Inscription", logout: "Déconnexion",
            dashboard: "Tableau de bord", profile: "Profil", settings: "Paramètres",
            language: "Langue", theme: "Thème", light: "Clair", dark: "Sombre",
            cancel: "Annuler", save: "Enregistrer", confirm: "Confirmer", continue: "Continuer",
            search: "Rechercher", clear_all: "Tout effacer" },
        nav: { how_it_works: "Fonctionnement", gift_cards: "Cartes", rates: "Tarifs",
            faq: "FAQ", contact: "Contact" },
        landing: { exchange_now: "Échanger", you_send: "Vous envoyez", you_receive: "Vous recevez" },
        send: { select_card: "Choisir carte", card_details: "Détails", payout: "Paiement",
            face_value: "Valeur", security_pin: "Code PIN", receipt: "Reçu d'achat",
            payout_method: "Mode de paiement", you_will_receive: "Vous recevrez", submit: "Soumettre" },
        notifications: { title: "Notifications", empty: "Tout est à jour !",
            mark_all_read: "Tout marquer lu", clear_all: "Tout effacer" },
        profile: { member_since: "Membre depuis", last_login: "Dernière connexion",
            total_exchanges: "Échanges", completed: "Terminés", pending: "En attente",
            rejected: "Rejetés", volume: "Volume", avg_rate: "Taux moyen", country: "Pays",
            language: "Langue", theme: "Thème" },
    },
    ar: {
        common: { home: "الرئيسية", login: "تسجيل الدخول", signup: "إنشاء حساب", logout: "خروج",
            dashboard: "لوحة التحكم", profile: "الملف الشخصي", settings: "الإعدادات",
            language: "اللغة", theme: "السمة", light: "فاتح", dark: "داكن",
            cancel: "إلغاء", save: "حفظ", confirm: "تأكيد", continue: "متابعة",
            search: "بحث", clear_all: "مسح الكل" },
        nav: { how_it_works: "كيف يعمل", gift_cards: "البطاقات", rates: "الأسعار",
            faq: "الأسئلة", contact: "اتصل بنا" },
        landing: { exchange_now: "استبدل الآن", you_send: "ترسل", you_receive: "تستلم" },
        send: { select_card: "اختر البطاقة", card_details: "التفاصيل", payout: "الدفع",
            face_value: "القيمة", security_pin: "الرقم السري", receipt: "إيصال الشراء",
            payout_method: "طريقة الدفع", you_will_receive: "ستستلم", submit: "إرسال" },
        notifications: { title: "الإشعارات", empty: "لا توجد إشعارات",
            mark_all_read: "تحديد الكل كمقروء", clear_all: "مسح الكل" },
        profile: { member_since: "عضو منذ", last_login: "آخر دخول",
            total_exchanges: "إجمالي الصفقات", completed: "مكتملة", pending: "قيد الانتظار",
            rejected: "مرفوضة", volume: "الحجم", avg_rate: "متوسط السعر", country: "الدولة",
            language: "اللغة", theme: "السمة" },
    },
};

export const LANGUAGES = [
    { code: "en", label: "English", flag: "🇺🇸" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "ar", label: "العربية", flag: "🇸🇦" },
];

export function I18nProvider({ children }) {
    const { user, refresh } = useAuth() || {};
    const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");

    // Sync from user object when it loads
    useEffect(() => {
        if (user?.language && user.language !== lang) {
            setLang(user.language);
            localStorage.setItem("lang", user.language);
        }
    }, [user?.language]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    }, [lang]);

    const setLanguage = async (next) => {
        setLang(next);
        localStorage.setItem("lang", next);
        if (user) {
            try { await api.patch("/users/me/preferences", { language: next }); refresh && refresh(); }
            catch (_) { /* ignore */ }
        }
    };

    const t = useMemo(() => {
        const dict = STRINGS[lang] || STRINGS.en;
        return (path) => {
            const parts = String(path).split(".");
            let cur = dict;
            for (const p of parts) { cur = cur?.[p]; if (cur == null) break; }
            if (cur == null) {
                // Fallback to English
                cur = STRINGS.en;
                for (const p of parts) { cur = cur?.[p]; if (cur == null) break; }
            }
            return cur ?? path;
        };
    }, [lang]);

    return (
        <I18nContext.Provider value={{ lang, setLanguage, t }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) return { lang: "en", setLanguage: () => {}, t: (k) => k };
    return ctx;
}
